import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { sellerKeys } from '../lib/queryKeys';

const VISIBILITY_REFRESH_MIN_HIDDEN_MS = 30_000;

/**
 * Refresh seller data when the user returns to the tab after being away.
 * Per-query polling + notification-driven invalidation handle ongoing updates.
 */
export function useSellerLiveSync(enabled: boolean) {
  const queryClient = useQueryClient();
  const hiddenAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const onVisibility = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
        return;
      }

      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt == null) return;

      const hiddenFor = Date.now() - hiddenAt;
      if (hiddenFor < VISIBILITY_REFRESH_MIN_HIDDEN_MS) return;

      void queryClient.invalidateQueries({
        queryKey: sellerKeys.all,
        refetchType: 'active',
      });
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enabled, queryClient]);
}
